import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">&mdash; {source}</cite>
    </blockquote>
  );
}

export function NorthEastLincolnshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="North East Lincolnshire Pothole Claims | 31.8% U-Roads in RED | Fixtyer"
        description="North East Lincolnshire's unclassified roads in RED condition nearly doubled to 31.82% while A-roads improved. See what the DfT AMBER rating, 4,728 pothole repairs in five years, and council CVI data mean for your claim."
        canonical="/councils/north-east-lincolnshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'North East Lincolnshire', url: '/councils/north-east-lincolnshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'North East Lincolnshire has a GREEN spend scorecard — can I still claim for pothole damage?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because projected 2025/26 capital spend of £7.25m exceeds the £4.127m DfT allocation, and 2024/25 capital spend reached £10.799m against £2.972m allocated. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. The overall and Condition scorecards are AMBER, and the council\'s own CVI data shows unclassified RED condition at 31.82% in 2024.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road in Grimsby or Cleethorpes?',
            answer: 'Unclassified roads make up 412.77km — roughly 67% of North East Lincolnshire\'s 617km carriageway network. CVI surveys cover one-third of the network each year, with the full network captured over a three-year cycle. RED condition on U-roads rose from 17.60% in 2020 to 31.82% in 2024. Your claim should focus on when your road was last CVI-surveyed, prior reports, and whether a three-year survey cycle was reasonable for a defect of your pothole\'s size and age.',
          },
          {
            question: 'Does the 2022 CVI survey disagreement help my claim?',
            answer: 'Potentially. The council reports that 11km of the 2022 CVI survey network deteriorated despite surface treatment the year before, and that analysis with the surveying contractor "concluded with disagreement on the findings." The 2022 survey network is scheduled for CVI resurveying in 2025/26. That is documented uncertainty about condition data on the road type that makes up two-thirds of the borough — relevant when the council relies on Section 58 inspection records.',
          },
          {
            question: 'The council repaired 978 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. North East Lincolnshire repaired 978 defect records in 2024/25, down from a five-year peak of 1,243 in 2023/24, but still averaging 946 repairs annually over five years — 4,728 in total. Each record can include multiple potholes. The council itself estimates 945 repairs for 2025/26 based on that average, while projecting 19.6km of resurfacing. Reactive volume alongside rising U-road RED percentages suggests defects routinely form between surveys.',
          },
          {
            question: 'How quickly does North East Lincolnshire repair reported potholes?',
            answer: 'The council\'s pothole guidance states Category 1 defects representing an immediate or imminent hazard are made safe or repaired within 24 hours. Category 2 defects are repaired between 14 and 63 working days depending on risk. The council delivers over 9,000 highway safety inspections annually. If you reported a defect before your incident and it was not repaired within these windows, that dated record strengthens a Section 41 argument.',
          },
          {
            question: 'A-roads are improving — does that block claims on classified roads?',
            answer: 'Not automatically, but expectations differ by road class. A-road RED condition fell from 2.5% in 2020 to 1.94% in 2024, and the council credits extensive A-road resurfacing. B and C roads are less clear-cut: GREEN share fell from 68.80% in 2020 to 67.5% in 2024, with amber rising to 28.9%. Claims on classified roads still depend on whether your specific defect was inspected and repaired in time — but the strongest structural arguments sit on the deteriorating unclassified network the council itself prioritises for "strategic attention."',
          },
        ]}
      />
      <ServiceSchema
        councilName="North East Lincolnshire Council"
        councilSlug="north-east-lincolnshire"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/north-east-lincolnshire"
        headline="North East Lincolnshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">North East Lincolnshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Almost one in three unclassified roads is now in RED condition
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            North East Lincolnshire earns a <strong className="text-emerald-400">GREEN</strong> Spend scorecard
            and invests well beyond its DfT allocation. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because CVI survey data shows unclassified
            RED condition rising from <strong className="text-rose-400">17.6% to 31.82% in five years</strong>,
            on a 412.77km estate network the council itself says requires &ldquo;strategic attention in the short
            to medium term,&rdquo; while principal A-roads genuinely improve.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">31.82%</div>
            <div className="text-white font-medium mt-2">Unclassified roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 17.60% in 2020 and 21.75% in 2022 — a near-doubling on the 412.77km road type that
              makes up 67% of the borough&apos;s 617km carriageway network.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and CVI data from North East Lincolnshire&apos;s own June 2025 transparency
            report — improving A-roads, flat B/C roads, deteriorating unclassified streets
          </p>

          {/* A-roads */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">A-roads (87.82km — 14.2% of network): improving</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">1.94%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">down from 2.5% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">18.64%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">down from 25.3% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">79.42%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 72.2% in 2020</div>
              </div>
            </div>
            <p className="text-emerald-800 text-sm">
              Credit where due: the council lists extensive A180, A1098 and A1136 resurfacing schemes, and
              SCANNER data confirms principal routes are genuinely improving. A-roads are just one-seventh of
              the network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (116.59km — 18.9% of network): mixed</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3.20%</td>
                    <td className="px-4 py-2 text-center">28.00%</td>
                    <td className="px-4 py-2 text-center">68.80%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3.55%</td>
                    <td className="px-4 py-2 text-center">32.65%</td>
                    <td className="px-4 py-2 text-center">63.81%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3.20%</td>
                    <td className="px-4 py-2 text-center">28.03%</td>
                    <td className="px-4 py-2 text-center">68.77%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.0%</td>
                    <td className="px-4 py-2 text-center">25.5%</td>
                    <td className="px-4 py-2 text-center">75.5%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3.6%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">28.9%</td>
                    <td className="px-4 py-2 text-center font-bold">67.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C RED has stayed near 3–3.6%, but amber climbed back to 28.9% and green fell to 67.5% —
              roughly one-third of local classified roads now need maintenance soon or imminently.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Well-funded, but U-roads still slip</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£4.127m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£7.25m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">90%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share (2025/26)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              In 2024/25 the council spent <span className="text-emerald-400">£10.799m</span> in capital
              against a DfT allocation of just £2.972m — more than triple the formula grant — with 93%
              classed as preventative. The problem is not the chequebook on classified roads. It is the
              unclassified network where RED condition keeps climbing despite 10km of annual surface
              treatment.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road deterioration */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 412.77km unclassified deterioration
          </h2>
          <p className="text-surface-600 mb-8">
            67% of the network surveyed by CVI — one-third each year, full cycle every three years
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17.60%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20.40%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">21.75%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">31.60%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">31.82%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The three-year CVI cycle</h3>
              <p className="text-surface-600 text-sm mb-3">
                Unlike SCANNER-surveyed A and B/C roads, unclassified data is collected via Coarse Visual
                Inspection surveys covering one-third of the network annually — a full pass every three years.
                There is no DfT requirement to survey or report U-road condition at all; the council publishes
                this voluntarily.
              </p>
              <p className="text-surface-600 text-sm">
                At 31.82% RED, roughly <strong>131km of residential and estate roads</strong> were in the
                should-be-maintained category at the last survey — on a network where the council itself notes
                the national average unclassified resurfacing interval is every 134 years.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 2022 survey anomaly</h3>
              <p className="text-amber-800 text-sm mb-3">
                CVI surveys in 2022 produced a &ldquo;noticeable deterioration&rdquo; the council found surprising:
                11km deteriorated despite surface treatment the year before. Analysis with the surveying
                contractor &ldquo;concluded with disagreement on the findings.&rdquo;
              </p>
              <p className="text-amber-800 text-sm">
                The 2022 survey network is scheduled for CVI resurveying in 2025/26 with plans to compare
                findings. Until then, condition records on affected routes carry documented uncertainty.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For North East Lincolnshire&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last CVI-surveyed — and was it in the 2022 cohort now being resurveyed?</li>
              <li>• If 31.82% of U-roads were RED at the last survey, what was done about yours between three-year passes?</li>
              <li>• Does a coarse visual inspection every three years meet the standard for estate streets with rising defect rates?</li>
              <li>• Can the council reconcile 11km of unexplained 2022 deterioration with its inspection records?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of a network it only CVI-surveys in thirds over three
              years — while its own data shows RED condition nearly doubling.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            4,728 pothole repairs in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Defect records repaired on a 617km network — the council defines a pothole as 40mm deep and
            300mm or greater in diameter
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Financial year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Defect records repaired</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">903</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">872</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">732</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,243</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">978</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,728</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 2023/24 spike</h3>
              <p className="text-surface-600 text-sm">
                Repairs jumped 70% from 732 in 2022/23 to 1,243 in 2023/24 — the same period U-road RED
                condition leapt from 21.75% to 31.60%. The council projects 945 repairs for 2025/26 based
                on the five-year average, alongside 19.6km of planned resurfacing across 65 roads.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The counting caveat</h3>
              <p className="text-amber-800 text-sm">
                The council&apos;s own footnote: a defect record &ldquo;will include one or more potholes.&rdquo;
                Figures cover defects found by highway inspectors during safety inspections and those reported
                by the public. The true number of individual potholes treated is therefore{' '}
                <strong>higher</strong> than the published totals.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Financial year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Capital spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">Preventative</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">£2,706k</td>
                  <td className="px-6 py-4 text-center">£6,731k</td>
                  <td className="px-6 py-4 text-center">£2,183k</td>
                  <td className="px-6 py-4 text-center">92%</td>
                  <td className="px-6 py-4 text-center">8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">£2,338k</td>
                  <td className="px-6 py-4 text-center">£6,511k</td>
                  <td className="px-6 py-4 text-center">£2,117k</td>
                  <td className="px-6 py-4 text-center">92%</td>
                  <td className="px-6 py-4 text-center">8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">£2,972k</td>
                  <td className="px-6 py-4 text-center">£3,865k</td>
                  <td className="px-6 py-4 text-center">£1,860k</td>
                  <td className="px-6 py-4 text-center">86%</td>
                  <td className="px-6 py-4 text-center">14%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">£2,491k</td>
                  <td className="px-6 py-4 text-center">£7,331k</td>
                  <td className="px-6 py-4 text-center">£2,245k</td>
                  <td className="px-6 py-4 text-center">91%</td>
                  <td className="px-6 py-4 text-center">9%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">£2,972k</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">£10,799k</td>
                  <td className="px-6 py-4 text-center">£2,067k</td>
                  <td className="px-6 py-4 text-center">93%</td>
                  <td className="px-6 py-4 text-center">7%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 projected</td>
                  <td className="px-6 py-4 text-center">£4,127k</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">£7,250k</td>
                  <td className="px-6 py-4 text-center">£2,149k</td>
                  <td className="px-6 py-4 text-center">90%</td>
                  <td className="px-6 py-4 text-center">10%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Resurfacing focus */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Resurfacing follows the deterioration
          </h2>
          <p className="text-surface-600 mb-8">
            Kilometres resurfaced by road class — unclassified roads absorb the bulk of treatment
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">A roads (km)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">B roads (km)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">C roads (km)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Unclassified (km)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">6.60</td>
                  <td className="px-4 py-3 text-center">1.90</td>
                  <td className="px-4 py-3 text-center">0.30</td>
                  <td className="px-4 py-3 text-center">13.50</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">1.00</td>
                  <td className="px-4 py-3 text-center">2.10</td>
                  <td className="px-4 py-3 text-center">1.20</td>
                  <td className="px-4 py-3 text-center">16.00</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">4.03</td>
                  <td className="px-4 py-3 text-center">0.23</td>
                  <td className="px-4 py-3 text-center">0</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-600">15.89</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">4.03</td>
                  <td className="px-4 py-3 text-center">0.69</td>
                  <td className="px-4 py-3 text-center">1.16</td>
                  <td className="px-4 py-3 text-center">10.31</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">3.70</td>
                  <td className="px-4 py-3 text-center">0</td>
                  <td className="px-4 py-3 text-center">1.14</td>
                  <td className="px-4 py-3 text-center">7.87</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 projected</td>
                  <td className="px-4 py-3 text-center">2.81</td>
                  <td className="px-4 py-3 text-center">0.12</td>
                  <td className="px-4 py-3 text-center">0.88</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-600">15.80</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Analysis of our local road condition data indicates that our unclassified road network is the portion of network that requires strategic attention in the short to medium term, as illustrated in figure 3."
              source="North East Lincolnshire Council Highway Maintenance Transparency Report, June 2025"
            />
            <QuoteCard
              quote="The unclassified network makes up the largest percentage of the highway road network in North East Lincolnshire and CVI surveys undertaken in 2022 resulted in a noticeable deterioration in condition."
              source="North East Lincolnshire Council Highway Maintenance Transparency Report, June 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council formally identifies unclassified roads — not A-roads — as the priority for
                strategic maintenance. That is documented knowledge that two-thirds of the network is
                deteriorating faster than classified routes, despite annual surface treatment of approximately
                10km on U-roads.
              </p>
              <p className="text-surface-600 text-sm">
                The 2025/26 Local Transport Plan includes Moody Lane reconstruction, large-scale pothole-fund
                patching on Victoria Street North and Middlethorpe Road, and surface treatments across dozens
                of Grimsby, Cleethorpes and Humberston estate streets.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road on the 2025/26 LTP programme — or left to reactive repair only?</li>
                <li>• Did the council increase CVI frequency after RED hit 31.6% in 2023?</li>
                <li>• If 11km deteriorated despite prior treatment in 2022, why wasn&apos;t your defect caught?</li>
                <li>• Does thermal pothole patching address structural failure — or just the surface symptom?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a well-funded AMBER council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: North East Lincolnshire invests seriously — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend consistently exceeds DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 86–93% preventative maintenance share over five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition genuinely improving on SCANNER data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Over 9,000 annual safety inspections and documented asset management under Well Managed Highway Infrastructure 2016
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on principal A-roads. Generic claims will struggle there.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — U-road RED nearly doubled from 17.6% to 31.82%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 67% of the network on a three-year CVI cycle, not continuous SCANNER monitoring
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Documented 2022 CVI disagreement — 11km deteriorated despite prior treatment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 4,728 pothole repairs in five years — including a 1,243 peak in 2023/24
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council&apos;s own admission that U-roads need &ldquo;strategic attention&rdquo; short to medium term
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and 90%+ preventative ratios, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road class — on a U-road, the three-year CVI gap and 31.82% RED rate are your strongest structural arguments</li>
              <li>• Whether the defect meets the council&apos;s own 40mm/300mm pothole definition — or was a sub-threshold defect left unrepaired</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites North East Lincolnshire&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-50 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to North East Lincolnshire Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting creates a record the council cannot ignore — Category 1 hazards should be made safe
            within 24 hours; Category 2 defects within 14–63 working days. Prior reports strengthen Section 58
            rebuttals if you later claim for damage.
          </p>
          <a
            href="https://www.nelincs.gov.uk/streets-travel-and-parking/roadworks-and-road-repairs/potholes/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — Roads, footpaths and cycleways form
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in North East Lincolnshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No three-year CVI cycle argument</li>
                <li>• No prior-report search</li>
                <li>• No 2022 survey disagreement cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 31.82% U-road RED deterioration documented</li>
                <li>✅ Three-year CVI survey gap argued</li>
                <li>✅ 4,728 pothole repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to North East Lincolnshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=north-east-lincolnshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my North East Lincolnshire claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                North East Lincolnshire has a GREEN spend scorecard — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because projected 2025/26 capital spend of £7.25m exceeds
                the £4.127m DfT allocation, and 2024/25 capital spend reached £10.799m against £2.972m allocated.
                Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected
                and repaired — not on aggregate spend. The overall and Condition scorecards are AMBER, and the
                council&apos;s own CVI data shows unclassified RED condition at 31.82% in 2024.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road in Grimsby or Cleethorpes?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 412.77km — roughly 67% of North East Lincolnshire&apos;s 617km
                carriageway network. CVI surveys cover one-third of the network each year, with the full network
                captured over a three-year cycle. RED condition on U-roads rose from 17.60% in 2020 to 31.82% in
                2024. Your claim should focus on when your road was last CVI-surveyed, prior reports, and whether
                a three-year survey cycle was reasonable for a defect of your pothole&apos;s size and age.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the 2022 CVI survey disagreement help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially. The council reports that 11km of the 2022 CVI survey network deteriorated despite
                surface treatment the year before, and that analysis with the surveying contractor &ldquo;concluded
                with disagreement on the findings.&rdquo; The 2022 survey network is scheduled for CVI resurveying
                in 2025/26. That is documented uncertainty about condition data on the road type that makes up
                two-thirds of the borough — relevant when the council relies on Section 58 inspection records.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The council repaired 978 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. North East Lincolnshire repaired 978 defect records in 2024/25, down from a five-year peak of
                1,243 in 2023/24, but still averaging 946 repairs annually over five years — 4,728 in total. Each
                record can include multiple potholes. The council itself estimates 945 repairs for 2025/26 based
                on that average, while projecting 19.6km of resurfacing. Reactive volume alongside rising U-road
                RED percentages suggests defects routinely form between surveys.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How quickly does North East Lincolnshire repair reported potholes?
              </h3>
              <p className="text-surface-600 text-sm">
                The council&apos;s pothole guidance states Category 1 defects representing an immediate or imminent
                hazard are made safe or repaired within 24 hours. Category 2 defects are repaired between 14 and 63
                working days depending on risk. The council delivers over 9,000 highway safety inspections annually.
                If you reported a defect before your incident and it was not repaired within these windows, that
                dated record strengthens a Section 41 argument.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                A-roads are improving — does that block claims on classified roads?
              </h3>
              <p className="text-surface-600 text-sm">
                Not automatically, but expectations differ by road class. A-road RED condition fell from 2.5% in 2020
                to 1.94% in 2024, and the council credits extensive A-road resurfacing. B and C roads are less
                clear-cut: GREEN share fell from 68.80% in 2020 to 67.5% in 2024, with amber rising to 28.9%.
                Claims on classified roads still depend on whether your specific defect was inspected and repaired in
                time — but the strongest structural arguments sit on the deteriorating unclassified network the council
                itself prioritises for strategic attention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.nelincs.gov.uk/assets/uploads/2025/06/Highway-Maintenance-transparency-report-2025.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              North East Lincolnshire Council Highway Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
